'use client'

import { nextPlaybackRate } from '@/lib/playbackRate'
import { RefObject, useCallback, useEffect, useReducer, useRef, useState } from 'react'

// Playback-related reducer
type PlaybackState = {
  playbackRate: number
  isMuted: boolean
}

type PlaybackAction =
  | { type: 'CYCLE_PLAYBACK_RATE' }
  | { type: 'TOGGLE_MUTE' }

const playbackInitialState: PlaybackState = {
  playbackRate: 1,
  isMuted: false,
}

function playbackReducer(state: PlaybackState, action: PlaybackAction): PlaybackState {
  switch (action.type) {
    case 'CYCLE_PLAYBACK_RATE':
      return { ...state, playbackRate: nextPlaybackRate(state.playbackRate) }
    case 'TOGGLE_MUTE':
      return { ...state, isMuted: !state.isMuted }
  }
}

export type SPState = 'playing' | 'paused' | 'detail'

export interface UseSPVideoPlaybackParams {
  videoRef: RefObject<HTMLVideoElement | null>
  currentVideoIndex: number
  /** 再生中の動画の nodeKey。マウント起点の自動再生をこの単位で1回に抑える */
  nodeKey?: string
  /**
   * マウント起点の自動再生を済ませた動画（nodeKey）。VideoModal が保持する。
   * PC/SP の切替で <video> が作り直されても、視聴者が止めた動画を再生し直さない。
   */
  autoPlayedNodeKeyRef?: { current: string | null }
  showEndScreen: boolean
  /** 評価を閉じたあとに同じ動画を再生し直すか（回答後は true / スキップは false） */
  replayAfterEndScreen: boolean
  onVideoEnd: () => void
}

export interface UseSPVideoPlaybackReturn {
  state: SPState
  setState: (state: SPState) => void
  showControls: boolean
  setShowControls: (show: boolean) => void
  playbackState: PlaybackState
  shouldAutoPlayRef: RefObject<boolean>
  handleVideoTap: () => void
  handlePlay: () => void
  handleVideoEnded: () => void
  handlePlaybackRateChange: () => void
  handleMuteToggle: () => void
}

export function useSPVideoPlayback({
  videoRef,
  currentVideoIndex,
  nodeKey,
  autoPlayedNodeKeyRef,
  showEndScreen,
  replayAfterEndScreen,
  onVideoEnd,
}: UseSPVideoPlaybackParams): UseSPVideoPlaybackReturn {
  const [state, setState] = useState<SPState>('paused')
  const [showControls, setShowControls] = useState(true)
  const [playbackState, playbackDispatch] = useReducer(playbackReducer, playbackInitialState)
  const shouldAutoPlayRef = useRef(false)
  const isInitialMountRef = useRef(true)
  const prevShowEndScreenRef = useRef(showEndScreen)

  // Handle video tap (play/pause)
  const handleVideoTap = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play()
      setState((prev) => (prev === 'detail' ? 'detail' : 'playing'))
      setState((prev) => { if (prev !== 'detail') setShowControls(false); return prev })
    } else {
      video.pause()
      setState((prev) => (prev === 'detail' ? 'detail' : 'paused'))
      setState((prev) => { if (prev !== 'detail') setShowControls(true); return prev })
    }
  }, [videoRef])

  // Handle play from controls
  const handlePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    video.play()
    setState('playing')
    setShowControls(false)
  }, [videoRef])

  // Handle video end
  const handleVideoEnded = useCallback(() => {
    setState('paused')
    setShowControls(true)
    onVideoEnd()
  }, [onVideoEnd])

  // Handle playback rate change
  const handlePlaybackRateChange = useCallback(() => {
    playbackDispatch({ type: 'CYCLE_PLAYBACK_RATE' })
    // dispatch 後の値をここでも計算して <video> へ即時反映
    const newRate = nextPlaybackRate(playbackState.playbackRate)
    if (videoRef.current) {
      videoRef.current.playbackRate = newRate
    }
  }, [playbackState.playbackRate, videoRef])

  // A4: Handle mute toggle
  const handleMuteToggle = useCallback(() => {
    playbackDispatch({ type: 'TOGGLE_MUTE' })
    if (videoRef.current) {
      videoRef.current.muted = !playbackState.isMuted
    }
  }, [videoRef, playbackState.isMuted])

  // Apply playback rate when video changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackState.playbackRate
    }
  }, [currentVideoIndex, playbackState.playbackRate, videoRef])

  // Sync mute state when video changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = playbackState.isMuted
    }
  }, [currentVideoIndex, playbackState.isMuted, videoRef])

  // Auto-play on initial mount
  useEffect(() => {
    const video = videoRef.current
    if (video) {
      // PC/SP の切替で作り直されただけなら再生し直さない。視聴者が止めた動画は止めたまま。
      if (nodeKey && autoPlayedNodeKeyRef?.current === nodeKey) {
        isInitialMountRef.current = false
        setState(video.paused ? 'paused' : 'playing')
        setShowControls(video.paused)
        return
      }
      setTimeout(() => {
        if (nodeKey && autoPlayedNodeKeyRef) {
          autoPlayedNodeKeyRef.current = nodeKey
        }
        video.play()
        video.playbackRate = playbackState.playbackRate
        setState('playing')
        setShowControls(false)
        isInitialMountRef.current = false
      }, 100)
    }
     
  }, [])

  // Reset state when video changes, auto-play always
  useEffect(() => {
    // Skip on initial mount (handled by above useEffect)
    if (isInitialMountRef.current) {
      return
    }

    if (shouldAutoPlayRef.current) {
      shouldAutoPlayRef.current = false
    }

    // Always auto-play on video change
    const video = videoRef.current
    if (video) {
      setTimeout(() => {
        if (nodeKey && autoPlayedNodeKeyRef) {
          autoPlayedNodeKeyRef.current = nodeKey
        }
        video.play()
        video.playbackRate = playbackState.playbackRate
        setState('playing')
        setShowControls(false)
      }, 100)
    }
     
  }, [currentVideoIndex])

  // Auto-play when transitioning from end screen (replay)
  useEffect(() => {
    // Detect transition from end screen to playing (replay scenario)
    // スキップで閉じたときは再生し直さない。動画は終了状態（最終フレーム＋中央のコントロール）
    // のまま止め、もう一度見るか・別の動画へ行くか・CTAへ進むかは視聴者に選ばせる。
    if (prevShowEndScreenRef.current && !showEndScreen && replayAfterEndScreen) {
      const video = videoRef.current
      if (video) {
        setTimeout(() => {
          video.currentTime = 0
          video.play()
          video.playbackRate = playbackState.playbackRate
          setState('playing')
          setShowControls(false)
        }, 100)
      }
    }
    prevShowEndScreenRef.current = showEndScreen
  }, [showEndScreen, replayAfterEndScreen, videoRef, playbackState.playbackRate])

  return {
    state,
    setState,
    showControls,
    setShowControls,
    playbackState,
    shouldAutoPlayRef,
    handleVideoTap,
    handlePlay,
    handleVideoEnded,
    handlePlaybackRateChange,
    handleMuteToggle,
  }
}
